import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, Plus } from 'lucide-react';
import { MoneyInput } from '../../components/MoneyInput';
import { RowGroup, RowField } from '../../components/RowField';
import { Sheet } from '../../components/Sheet';
import { StickyActionBar } from '../../components/StickyActionBar';
import { TearLine } from '../../components/TearLine';
import { Button } from '../../components/Button';
import { controlClass } from '../../components/FormSection';
import { useCustomerList } from '../customers/hooks';
import { useCreateDeposit, useCustomerDepositBalance } from './hooks';
import { accountBalanceDisplay } from './accountBalance';
import { KIND_OPTIONS } from './kindLabel';
import { usePageTitle } from '../../hooks/usePageTitle';
import {
  addLineItem,
  emptyLineItem,
  lineItemValid,
  removeLineItem,
  subtotalOf,
  updateLineItem,
  type EditableLineItem
} from './receiptLineItems';
import type { ReceiptKind } from './types';

export function NewDepositPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [customerId, setCustomerId] = useState(params.get('customerId') ?? '');
  const [kind, setKind] = useState<ReceiptKind>('Final');
  // Item 3: zero pre-created rows — items are added via the sheet.
  const [lineItems, setLineItems] = useState<EditableLineItem[]>([]);
  const [notes, setNotes] = useState('');
  const [editing, setEditing] = useState<{ idx: number | null } | null>(null);

  const docType = kind === 'Deposit' ? 'Deposit' : 'Invoice';
  usePageTitle(`New ${docType}`);

  const create = useCreateDeposit();
  const { data: balanceData } = useCustomerDepositBalance(customerId || undefined);
  const balanceDisplay = balanceData ? accountBalanceDisplay(balanceData.depositBalance) : null;

  const subtotal = useMemo(() => subtotalOf(lineItems), [lineItems]);
  const canSubmit = customerId !== '' && lineItems.length > 0;

  const onSubmit = async () => {
    const created = await create.mutateAsync({
      customerId,
      kind,
      lineItems: lineItems.map((i) => ({ description: i.description.trim(), quantity: 1, unitPrice: i.unitPrice })),
      notes: notes.trim() || null
    });
    nav(`/receipts/${created.id}`);
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-6 pb-24 sm:pb-8">
      <div>
        <Link to="/receipts" className="text-sm text-slate-500 hover:text-pine">← Receipts</Link>
        <h1 className="mt-2 font-display text-2xl font-semibold text-ink">New {docType}</h1>
      </div>

      <RowGroup title="Details">
        <RowField
          type="select"
          label="Type"
          value={kind}
          options={KIND_OPTIONS.map((k) => ({ value: k.value, label: k.label }))}
          onCommit={(v) => setKind(v as ReceiptKind)}
        />
        <ClientRow customerId={customerId} onPick={setCustomerId} />
      </RowGroup>

      {customerId && balanceDisplay && (
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm">
          <span className="text-slate-600">
            Account balance
            {balanceDisplay.helper && <span className="ml-1 text-xs text-slate-400">· {balanceDisplay.helper}</span>}
          </span>
          <span className={`font-mono font-semibold ${balanceDisplay.className}`}>{balanceDisplay.text}</span>
        </div>
      )}

      <section className="space-y-1.5">
        <h2 className="px-1 font-display text-sm font-semibold text-slate-500">Line items</h2>
        <div className="overflow-hidden rounded-2xl bg-surface shadow-sm ring-1 ring-slate-200/70 divide-y divide-slate-100">
          {lineItems.map((item, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setEditing({ idx })}
              className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
            >
              <span className="min-w-0 flex-1 truncate text-ink">{item.description || 'Untitled item'}</span>
              <span className="font-mono text-sm text-ink">{item.unitPrice.toFixed(2)}</span>
              <ChevronRight size={16} className="shrink-0 text-slate-400" />
            </button>
          ))}
          <button
            type="button"
            onClick={() => setEditing({ idx: null })}
            className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-pine hover:bg-slate-50"
          >
            <Plus size={16} /> Add item
          </button>
        </div>
        {lineItems.length > 0 && (
          <div className="px-1">
            <TearLine className="mt-2" />
            <div className="flex items-center justify-between pt-2 text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span className="font-mono font-semibold text-ink">{subtotal.toFixed(2)}</span>
            </div>
          </div>
        )}
      </section>

      <RowGroup title="Notes">
        <RowField type="multiline" label="Notes" value={notes} placeholder="Add notes" onCommit={setNotes} />
      </RowGroup>

      {create.isError && <p className="text-sm text-danger">Create failed — check your inputs and try again.</p>}

      <StickyActionBar total={<>Subtotal {subtotal.toFixed(2)}</>}>
        <Link to="/receipts" className="text-sm text-slate-600 hover:text-pine">Cancel</Link>
        <Button type="button" onClick={onSubmit} disabled={!canSubmit || create.isPending}>
          {create.isPending ? 'Creating…' : `Create ${docType.toLowerCase()}`}
        </Button>
      </StickyActionBar>

      {editing && (
        <LineItemSheet
          initial={editing.idx == null ? emptyLineItem() : lineItems[editing.idx]}
          canRemove={editing.idx != null}
          onSave={(item) =>
            setLineItems((li) => (editing.idx == null ? addLineItem(li, item) : updateLineItem(li, editing.idx!, item)))
          }
          onRemove={() => editing.idx != null && setLineItems((li) => removeLineItem(li, editing.idx!))}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function LineItemSheet({
  initial,
  canRemove,
  onSave,
  onRemove,
  onClose
}: {
  initial: EditableLineItem;
  canRemove: boolean;
  onSave: (item: EditableLineItem) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<EditableLineItem>(initial);
  const valid = lineItemValid(draft);

  return (
    <Sheet
      open
      onClose={onClose}
      title="Item"
      footer={
        <div className="flex items-center justify-between gap-2">
          {canRemove ? (
            <Button variant="ghost" onClick={() => { onRemove(); onClose(); }}>Remove</Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button disabled={!valid} onClick={() => { onSave(draft); onClose(); }}>Save</Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Description</span>
          <textarea
            autoFocus
            className={`${controlClass} min-h-[80px]`}
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-ink">Amount</span>
          <MoneyInput value={draft.unitPrice} onChange={(v) => setDraft((d) => ({ ...d, unitPrice: v }))} ariaLabel="Amount" />
        </label>
      </div>
    </Sheet>
  );
}

function ClientRow({ customerId, onPick }: { customerId: string; onPick: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data } = useCustomerList({ search, includeInactive: false, page: 1, pageSize: 25 });
  const selected = data?.items.find((c) => c.id === customerId);

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-slate-50">
        <span className="text-sm font-medium text-ink">Client</span>
        <span className="flex items-center gap-1.5 text-sm">
          <span className={`${selected ? 'text-ink' : 'text-slate-400'} max-w-[9rem] truncate sm:max-w-[14rem]`}>
            {selected ? selected.displayName : 'Pick a client'}
          </span>
          <ChevronRight size={16} className="shrink-0 text-slate-400" />
        </span>
      </button>
      {open && (
        <Sheet open onClose={() => setOpen(false)} title="Pick a client">
          <input
            autoFocus
            type="search"
            placeholder="Search clients…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${controlClass} mb-3`}
          />
          <ul className="space-y-1">
            {data?.items.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => { onPick(c.id); setOpen(false); }}
                  className="w-full rounded-lg px-3 py-3 text-left text-sm text-ink hover:bg-slate-50"
                >
                  <span className="block font-medium">{c.displayName}</span>
                  {c.email && <span className="block text-xs text-slate-400">{c.email}</span>}
                </button>
              </li>
            ))}
            {data && data.items.length === 0 && <li className="px-3 py-2 text-sm text-slate-500">No clients found.</li>}
          </ul>
        </Sheet>
      )}
    </>
  );
}
